import { useCallback, useEffect, useId, useMemo, useState } from 'react'
import { Navigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { FeatherIcon } from '../components/FeatherIcon'
import { LoadingBlock, Spinner, BusyLabel } from '../components/Spinner'
import { PageHeading, PageTabs } from '../components/PageHeader'
import { useToolAccess } from '../hooks/useToolAccess'
import { accountName, useAgo } from '../dynamicLists/hooks'
import { AccountSelect, ConfirmDialog, Modal } from '../dynamicLists/shared'
import type { CrmAccount } from '../dynamicLists/types'
import { withToolAccounts } from '../dynamicLists/api'
import { trackingLinksApi, type TrackingLinksApi } from './api'
import { columnsFor, defaultColumns, toneOf } from './columnDefs'
import { ColumnsPicker, HowItWorks } from './columns'
import { count, money, percent, rangeOf, reportUrl, type RangeChoice } from './format'
import { LinkDetailView } from './LinkDetail'
import { RangePicker } from './parts'
import { isRangeChoice, useStored } from './stored'
import { AddToReportDialog, ReportDialog } from './ReportDialog'
import type { Earning, LinkKind, LinkRef, LinkReport, LinkRow, LinkTotals, SortKey, SyncState } from './types'
import '../dynamicLists/dynamicLists.css'
import './trackingLinks.css'

const PAGE = 100
const TAB_KEY = 'tl.tab'

/** Tracking links (ADR 0032, 0039): the Tool's launch stage (ADR 0031), then only the
 *  Accounts the login holds access_tracking_links on (the CRM routes check the same). */
export function TrackingLinksPage() {
  const tools = useToolAccess()
  const ids = tools?.tool_accounts?.tracking_links
  const api = useMemo(() => withToolAccounts(trackingLinksApi, ids), [ids])
  if (!tools) return null
  if (!tools.usable.tracking_links) return <Navigate to="/" replace />
  return <TrackingLinksView api={api} />
}

type Tab = LinkKind | 'reports'

function readTab(): Tab {
  try {
    const v = localStorage.getItem(TAB_KEY)
    return v === 'reports' || v === 'trial' ? v : 'tracking'
  } catch {
    return 'tracking'
  }
}

const DEFAULT_RANGE: RangeChoice = { preset: 'all', custom: { from: null, to: null } }
const isEarning = (v: unknown): v is Earning => v === 'net' || v === 'gross'

/**
 * Every Account's tracking links and trial links, each kind on its own tab, with
 * what they brought in over a date range; and the public Link reports made from
 * them (CONTEXT.md, "Tracking links").
 */
export function TrackingLinksView({ api }: { api: TrackingLinksApi }) {
  const { t } = useTranslation('trackingLinks')
  const [tab, setTabState] = useState<Tab>(readTab)
  const [accounts, setAccounts] = useState<CrmAccount[] | null>(null)
  const [accountsError, setAccountsError] = useState('')
  // The range and net/gross carry across both link tabs.
  const [range, setRange] = useStored<RangeChoice>('tl.range', DEFAULT_RANGE, isRangeChoice)
  const [earning, setEarning] = useStored<Earning>('tl.earning', 'net', isEarning)
  const setTab = (next: string) => {
    setTabState(next as Tab)
    try {
      localStorage.setItem(TAB_KEY, next)
    } catch {
      // per-viewer convenience only
    }
  }

  useEffect(() => {
    api.accounts().then(
      (rows) => setAccounts(rows.filter((a) => (a.platform || 'onlyfans') === 'onlyfans')),
      (e: unknown) => setAccountsError(e instanceof Error ? e.message : String(e)),
    )
  }, [api])

  return (
    <div className="mobile-page-shell cpt-root dl-page tl-page w-full px-6 pt-5 pb-6 min-w-0">
      <PageHeading title={t('title')} subtitle={t('subtitle')} />
      <PageTabs
        tabs={[
          { id: 'tracking', label: t('tabs.tracking'), icon: 'link' },
          { id: 'trial', label: t('tabs.trial'), icon: 'gift' },
          { id: 'reports', label: t('tabs.reports'), icon: 'share-2' },
        ]}
        active={tab}
        onChange={setTab}
      />
      {accountsError && <p className="dl-error">{t('accountsError', { message: accountsError })}</p>}
      {tab === 'reports' ? (
        <ReportsTab api={api} accounts={accounts} />
      ) : (
        <LinksTab key={tab} api={api} accounts={accounts} kind={tab} range={range} onRange={setRange} earning={earning} onEarning={setEarning} />
      )}
    </div>
  )
}

// ── Links ─────────────────────────────────────────────────────────────────────

interface Sort {
  sort: SortKey
  dir: 'asc' | 'desc'
}
const isSort = (v: unknown): v is Sort => typeof v === 'object' && v != null && typeof (v as Sort).sort === 'string' && ((v as Sort).dir === 'asc' || (v as Sort).dir === 'desc')
const isIds = (v: unknown): v is string[] => Array.isArray(v) && v.every((x) => typeof x === 'string')
const refOf = (r: LinkRow): LinkRef => ({ account_id: r.account_id, kind: r.kind, link_id: r.link_id })

function LinksTab({
  api,
  accounts,
  kind,
  range: rangeChoice,
  onRange,
  earning,
  onEarning,
}: {
  api: TrackingLinksApi
  accounts: CrmAccount[] | null
  kind: LinkKind
  range: RangeChoice
  onRange: (r: RangeChoice) => void
  earning: Earning
  onEarning: (e: Earning) => void
}) {
  const { t } = useTranslation('trackingLinks')
  const ago = useAgo()
  const [accountId, setAccountId] = useState('')
  const [search, setSearch] = useState('')
  const [query, setQuery] = useState('')
  const [showDeleted, setShowDeleted] = useState(false)
  const [archive, setArchive] = useState(false)
  const [order, setOrder] = useStored<Sort>(`tl.sort.${kind}`, { sort: 'created_at', dir: 'desc' }, isSort)
  const [shownCols, setShownCols] = useStored<string[]>(`tl.cols.${kind}`, defaultColumns(kind), isIds)
  const [rows, setRows] = useState<LinkRow[] | null>(null)
  const [total, setTotal] = useState(0)
  const [totals, setTotals] = useState<LinkTotals | null>(null)
  const [sync, setSync] = useState<SyncState[]>([])
  const [repliesSince, setRepliesSince] = useState<string | null>(null)
  const [error, setError] = useState('')
  const [notice, setNotice] = useState('')
  const [open, setOpen] = useState<LinkRow | null>(null)
  const [creating, setCreating] = useState(false)
  const [sharing, setSharing] = useState<LinkRow | null>(null)
  const [newReport, setNewReport] = useState<LinkRef[] | null>(null)
  const [loadingMore, setLoadingMore] = useState(false)
  const [exporting, setExporting] = useState(false)

  const range = rangeOf(rangeChoice)
  const cols = columnsFor(kind).filter((c) => shownCols.includes(c.id))

  // Search waits for the typing to stop.
  useEffect(() => {
    const timer = window.setTimeout(() => setQuery(search.trim()), 350)
    return () => window.clearTimeout(timer)
  }, [search])

  const listQuery = useMemo(
    () => ({ kind, accountId, search: query, includeDeleted: showDeleted, archived: archive, range, earning, sort: order.sort, dir: order.dir }),
    // range is derived from the stored choice
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [kind, accountId, query, showDeleted, archive, range.from, range.to, earning, order.sort, order.dir],
  )

  const load = useCallback(async () => {
    setError('')
    try {
      const r = await api.list({ ...listQuery, limit: PAGE })
      setRows(r.links)
      setTotal(r.total)
      setTotals(r.totals)
      setSync(r.sync)
      setRepliesSince(r.replies_since ?? null)
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    }
  }, [api, listQuery])

  useEffect(() => {
    setRows(null)
    void load()
  }, [load])

  const more = async () => {
    if (!rows) return
    setLoadingMore(true)
    try {
      const r = await api.list({ ...listQuery, limit: PAGE, offset: rows.length })
      setRows([...rows, ...r.links])
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setLoadingMore(false)
    }
  }

  const refresh = async () => {
    if (!accountId) return
    setNotice('')
    try {
      await api.refresh(accountId)
      setNotice(t('links.refreshAsked'))
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    }
  }

  const exportCsv = async () => {
    setExporting(true)
    setError('')
    try {
      const { filename, csv } = await api.exportCsv(listQuery)
      const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }))
      const a = document.createElement('a')
      a.href = url
      a.download = filename
      a.click()
      window.setTimeout(() => URL.revokeObjectURL(url), 1000)
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setExporting(false)
    }
  }

  const setArchived = async (row: LinkRow, archived: boolean) => {
    setError('')
    try {
      await api.archive(row.account_id, row.kind, row.link_id, archived)
      setNotice(t(archived ? 'archive.archivedNotice' : 'archive.restoredNotice', { name: row.name }))
      void load()
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    }
  }

  const sortBy = (key: SortKey) =>
    setOrder(order.sort === key ? { sort: key, dir: order.dir === 'desc' ? 'asc' : 'desc' } : { sort: key, dir: key === 'name' || key === 'profile' ? 'asc' : 'desc' })
  // A plain function, not a component: a component made here would remount (and drop focus) on every render.
  const th = (id: string, label: string, sort?: SortKey, num = false) => {
    const on = sort && order.sort === sort
    return (
      <th key={id} className={num ? 'num' : ''} aria-sort={on ? (order.dir === 'asc' ? 'ascending' : 'descending') : undefined} title={t(`colHelp.${id}`, { since: repliesSince ?? '', defaultValue: '' }) || undefined}>
        {sort ? (
          <button type="button" className={`tl-sort ${on ? 'is-on' : ''}`} onClick={() => sortBy(sort)}>
            {label}
            <FeatherIcon name={on ? (order.dir === 'asc' ? 'chevron-up' : 'chevron-down') : 'chevrons-down'} size={12} />
          </button>
        ) : (
          label
        )}
      </th>
    )
  }

  const accountSync = accountId ? sync.find((s) => s.account_id === accountId) : null

  return (
    <>
      <div className="dl-bar tl-filters">
        <div className="dl-bar-account">
          <span className="dl-label">{t('links.account')}</span>
          <AccountSelect accounts={accounts} value={accountId} onChange={setAccountId} />
        </div>
        <RangePicker value={rangeChoice} onChange={onRange} />
        <EarningSwitch value={earning} onChange={onEarning} />
        <label className="dl-check">
          <input type="checkbox" checked={showDeleted} onChange={(e) => setShowDeleted(e.target.checked)} />
          {t('links.showDeleted')}
        </label>
      </div>

      <Totals kind={kind} totals={totals} />

      <div className="dl-bar tl-tools">
        <label className="dl-search tl-search">
          <FeatherIcon name="search" size={14} />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t('links.search')} aria-label={t('links.search')} />
        </label>
        <ColumnsPicker kind={kind} value={shownCols} onChange={setShownCols} />
        <button type="button" className="dl-btn" onClick={() => void exportCsv()} disabled={exporting || !rows?.length} title={t('links.exportHint')}>
          <FeatherIcon name="download" size={14} /> {exporting ? t('links.exporting') : t('links.export')}
        </button>
        <button type="button" className={`dl-btn ${archive ? 'is-on' : ''}`} aria-pressed={archive} onClick={() => setArchive(!archive)} title={t('archive.hint')}>
          <FeatherIcon name="archive" size={14} /> {t('archive.button')}
        </button>
        <HowItWorks kind={kind} repliesSince={repliesSince} />
        <div className="dl-bar-actions">
          {accountId && (
            <button type="button" className="dl-btn" onClick={() => void refresh()} title={t('links.refreshHint')}>
              <FeatherIcon name="refresh-cw" size={14} /> {t('links.refresh')}
            </button>
          )}
          <button type="button" className="dl-btn dl-btn--primary" onClick={() => setCreating(true)} disabled={!accounts}>
            <FeatherIcon name="plus" size={14} /> {t(kind === 'trial' ? 'links.newTrial' : 'links.new')}
          </button>
        </div>
      </div>

      {archive && <p className="dl-notice tl-archive-note">{t('archive.viewing')}</p>}
      {accountSync && (
        <p className="dl-muted tl-sync">
          {accountSync.last_error
            ? t('links.syncError', { message: accountSync.last_error })
            : accountSync.last_synced_at
              ? t('links.synced', { ago: ago(accountSync.last_synced_at) })
              : t('links.neverSynced')}
          {accountSync.requested_at && (!accountSync.last_synced_at || accountSync.requested_at > accountSync.last_synced_at) && ` · ${t('links.refreshPending')}`}
          {kind === 'trial' && accountSync.trial_claims_error && ` · ${t('links.trialReadError', { message: accountSync.trial_claims_error })}`}
        </p>
      )}
      {error && (
        <p className="dl-error">
          {error}{' '}
          <button type="button" className="dl-link" onClick={() => void load()}>
            {t('retry')}
          </button>
        </p>
      )}
      {notice && <p className="dl-notice">{notice}</p>}

      {!rows ? (
        !error && <LoadingBlock label={t('loading')} />
      ) : rows.length === 0 ? (
        <div className="dl-empty">
          <FeatherIcon name={archive ? 'archive' : kind === 'trial' ? 'gift' : 'link'} size={22} />
          <p className="dl-strong">{query ? t('links.noMatch') : archive ? t('archive.emptyTitle') : t(`links.emptyTitle_${kind}`)}</p>
          <p className="dl-muted">{archive ? t('archive.emptyBody') : t(`links.emptyBody_${kind}`)}</p>
        </div>
      ) : (
        <div className="tl-table-wrap">
          <div className="tl-table-scroll">
            <table className="tl-table">
              <thead>
                <tr>
                  {th('link', t('col.link'), 'name')}
                  {th('profile', t('col.profile'), 'profile')}
                  {cols.map((c) => th(c.id, t(`col.${c.id}`), c.sort, c.num))}
                  <th className="tl-actions-head">
                    <span className="sr-only">{t('col.actions')}</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={`${r.account_id}/${r.kind}/${r.link_id}`} className={r.is_deleted ? 'is-deleted' : ''} onClick={() => setOpen(r)} tabIndex={0}
                    onKeyDown={(e) => e.target === e.currentTarget && (e.key === 'Enter' || e.key === ' ') && (e.preventDefault(), setOpen(r))}>
                    <td className="tl-cell-link">
                      <span className="dl-strong dl-ellipsis">{r.name}</span>
                      <span className="tl-sub">
                        {r.created_at && <span>{t('links.created', { date: r.created_at.slice(0, 10) })}</span>}
                        {r.kind === 'trial' && r.trial_days != null && <span>· {t('links.trialDays', { count: r.trial_days })}</span>}
                        {r.is_deleted && <span className="dl-badge dl-badge--quiet">{t('links.deleted')}</span>}
                        {r.kind === 'trial' && !r.is_deleted && r.is_finished && <span className="dl-badge dl-badge--quiet">{t('links.finished')}</span>}
                        {r.archived && <span className="dl-badge dl-badge--quiet">{t('archive.badge')}</span>}
                        {r.live_reports > 0 && <span className="dl-badge">{t('links.inReports', { count: r.live_reports })}</span>}
                      </span>
                    </td>
                    <td className="tl-cell-profile">
                      <span className="dl-ellipsis">{r.profile_name}</span>
                      {r.profile_username && <span className="tl-sub dl-ellipsis">@{r.profile_username}</span>}
                    </td>
                    {cols.map((c) => (
                      <td key={c.id} className={`${c.num ? 'num' : ''} ${c.id === 'updated' ? 'tl-cell-status' : ''} ${toneOf(c, r)}`} data-label={t(`col.${c.id}`)}>
                        {c.cell(r, t, ago)}
                      </td>
                    ))}
                    <td className="tl-actions" onClick={(e) => e.stopPropagation()}>
                      <button type="button" className="dl-iconbtn" onClick={() => setSharing(r)} aria-label={t('share.button', { name: r.name })} title={t('share.title')}>
                        <FeatherIcon name="share-2" size={14} />
                      </button>
                      <button type="button" className="dl-iconbtn" onClick={() => void setArchived(r, !r.archived)}
                        aria-label={t(r.archived ? 'archive.restore' : 'archive.archive', { name: r.name })} title={t(r.archived ? 'archive.restoreTitle' : 'archive.archiveTitle')}>
                        <FeatherIcon name={r.archived ? 'rotate-ccw' : 'archive'} size={14} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
              {totals && rows.length > 1 && (
                <tfoot>
                  <tr>
                    <td>{t('totals.row', { count: total })}</td>
                    <td />
                    {cols.map((c) => (
                      <td key={c.id} className={c.num ? 'num' : ''}>{c.total ? c.total(totals) : ''}</td>
                    ))}
                    <td />
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
          {rows.length < total && (
            <button type="button" className="dl-btn tl-more" onClick={() => void more()} disabled={loadingMore}>
              {loadingMore ? <Spinner size={14} label={t('loading')} /> : t('links.more', { shown: rows.length, total })}
            </button>
          )}
        </div>
      )}

      {open && (
        <LinkDetailView api={api} row={open} earning={earning} range={rangeChoice} onClose={() => setOpen(null)} onChanged={() => void load()}
          onArchive={(archived) => (void setArchived(open, archived), setOpen(null))} />
      )}
      {creating && accounts && (
        kind === 'trial' ? (
          <NewTrialDialog
            api={api}
            accounts={accounts}
            accountId={accountId}
            onClose={() => setCreating(false)}
            onCreated={(name) => {
              setCreating(false)
              setNotice(t('newTrial.createdNotice', { name }))
              void load()
            }}
          />
        ) : (
          <NewLinkDialog
            api={api}
            accounts={accounts}
            accountId={accountId}
            onClose={() => setCreating(false)}
            onCreated={(name) => {
              setCreating(false)
              setNotice(t('links.createdNotice', { name }))
              void load()
            }}
          />
        )
      )}
      {sharing && (
        <AddToReportDialog
          api={api}
          link={sharing}
          onClose={() => setSharing(null)}
          onNew={() => (setNewReport([refOf(sharing)]), setSharing(null))}
          onAdded={(report) => {
            setSharing(null)
            setNotice(t('share.addedNotice', { name: sharing.name, report: report.name }))
            void load()
          }}
        />
      )}
      {newReport && (
        <ReportDialog
          api={api}
          accounts={accounts}
          report={null}
          initialLinks={newReport}
          onClose={() => setNewReport(null)}
          onSaved={(r) => {
            setNewReport(null)
            setNotice(t('reports.createdNotice', { name: r.name }))
            void load()
          }}
        />
      )}
    </>
  )
}

/** The cards above the table. Totals count each fan and each payment once. */
function Totals({ kind, totals }: { kind: LinkKind; totals: LinkTotals | null }) {
  const { t } = useTranslation('trackingLinks')
  const tiles: Array<{ key: string; value: string; className?: string; note?: string }> = totals
    ? [
        { key: 'clicks', value: count(totals.clicks), note: totals.clicks_partial ? '*' : undefined },
        ...(kind === 'trial' ? [{ key: 'claims', value: count(totals.claims) }] : []),
        { key: 'fans', value: count(totals.fans) },
        { key: 'earnings', value: money(totals.earnings) },
        { key: 'cost', value: money(totals.cost) },
        { key: 'cpc', value: money(totals.cpc) },
        { key: 'cpf', value: money(totals.cpf) },
        { key: 'conversion', value: percent(totals.conversion) },
        { key: 'roi', value: percent(totals.roi), className: totals.roi == null ? '' : totals.roi >= 0 ? 'tl-good' : 'tl-bad' },
      ]
    : []
  return (
    <>
      <section className="tl-totals" aria-label={t('totals.label')} aria-busy={!totals}>
        {totals
          ? tiles.map((k) => (
              <div key={k.key} className="tl-kpi" title={t(`colHelp.${k.key}`, { since: '' })}>
                <span className="dl-label">{t(`totals.${k.key}`)}</span>
                <strong className={`tl-kpi-value ${k.className ?? ''}`}>
                  {k.value}
                  {k.note && <span className="tl-mark" title={t('kpi.clicksPartial')}>{k.note}</span>}
                </strong>
              </div>
            ))
          : Array.from({ length: kind === 'trial' ? 9 : 8 }, (_, i) => <div key={i} className="tl-kpi tl-kpi--loading" />)}
      </section>
      {totals && !totals.complete && totals.fans != null && <p className="tl-kpi-note tl-totals-note">{t('totals.incomplete')}</p>}
    </>
  )
}

export function EarningSwitch({ value, onChange }: { value: Earning; onChange: (e: Earning) => void }) {
  const { t } = useTranslation('trackingLinks')
  return (
    <div className="dl-seg" role="group" aria-label={t('earning.label')}>
      {(['net', 'gross'] as const).map((e) => (
        <button key={e} type="button" className={`dl-seg-btn ${value === e ? 'is-on' : ''}`} aria-pressed={value === e} onClick={() => onChange(e)}
          title={t(`earning.${e}Hint`)}>
          {t(`earning.${e}`)}
        </button>
      ))}
    </div>
  )
}

function CostField({ id, value, onChange, hint }: { id: string; value: string; onChange: (v: string) => void; hint: string }) {
  const { t } = useTranslation('trackingLinks')
  return (
    <div className="dl-field">
      <label className="dl-label" htmlFor={id}>{t('newLink.cost')}</label>
      <span className="dl-num-box tl-money">
        <span className="dl-muted">$</span>
        <input id={id} type="number" min={0} step="any" inputMode="decimal" value={value} onChange={(e) => onChange(e.target.value)}
          placeholder="0" aria-describedby={`${id}-hint`} />
      </span>
      <span id={`${id}-hint`} className="dl-hint">{hint}</span>
    </div>
  )
}

function NewLinkDialog({
  api,
  accounts,
  accountId: initialAccount,
  onClose,
  onCreated,
}: {
  api: TrackingLinksApi
  accounts: CrmAccount[]
  accountId: string
  onClose: () => void
  onCreated: (name: string) => void
}) {
  const { t } = useTranslation('trackingLinks')
  const [accountId, setAccountId] = useState(initialAccount)
  const [name, setName] = useState('')
  const [cost, setCost] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const ids = useId()
  const amount = cost.trim() === '' ? null : Number(cost)
  const problem = !accountId ? t('newLink.pickAccount') : !name.trim() ? t('newLink.nameNeeded') : amount != null && (!Number.isFinite(amount) || amount < 0) ? t('cost.badAmount') : ''

  const save = async () => {
    setBusy(true)
    setError('')
    try {
      await api.create(accountId, name.trim(), amount == null ? null : { amount })
      onCreated(name.trim())
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
      setBusy(false)
    }
  }

  return (
    <Modal
      title={t('newLink.title')}
      onClose={onClose}
      footer={
        <>
          {error && <p className="dl-error dl-foot-error">{error}</p>}
          <button type="button" className="dl-btn dl-btn--ghost" onClick={onClose}>
            {t('cancel')}
          </button>
          <button type="button" className="dl-btn dl-btn--primary" disabled={busy || Boolean(problem)} title={problem || undefined} onClick={() => void save()}>
            <BusyLabel busy={busy} label={t('saving')}>{t('newLink.create')}</BusyLabel>
          </button>
        </>
      }
    >
      <div className="tl-form">
        <div className="dl-field">
          <span className="dl-label">{t('links.account')}</span>
          <AccountSelect accounts={accounts} value={accountId} onChange={setAccountId} />
        </div>
        <div className="dl-field">
          <label className="dl-label" htmlFor={`${ids}-name`}>{t('newLink.name')}</label>
          <input id={`${ids}-name`} className="dl-input" value={name} maxLength={64} onChange={(e) => setName(e.target.value)} placeholder={t('newLink.namePlaceholder')} autoFocus />
        </div>
        <CostField id={`${ids}-cost`} value={cost} onChange={setCost} hint={t('newLink.costHint')} />
        <p className="dl-hint">{t('newLink.onlyFansNote')}</p>
      </div>
    </Modal>
  )
}

const TRIAL_DAY_CHOICES = [1, 3, 7, 14, 30, 60, 90, 180, 360]

/** A new trial link: name, free days and an optional cap on claims (OnlyFans takes no end date on create). */
function NewTrialDialog({
  api,
  accounts,
  accountId: initialAccount,
  onClose,
  onCreated,
}: {
  api: TrackingLinksApi
  accounts: CrmAccount[]
  accountId: string
  onClose: () => void
  onCreated: (name: string) => void
}) {
  const { t } = useTranslation('trackingLinks')
  const [accountId, setAccountId] = useState(initialAccount)
  const [name, setName] = useState('')
  const [days, setDays] = useState(30)
  const [cap, setCap] = useState('')
  const [cost, setCost] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const ids = useId()
  const amount = cost.trim() === '' ? null : Number(cost)
  const claimsCap = cap.trim() === '' ? null : Number(cap)
  const problem = !accountId
    ? t('newLink.pickAccount')
    : !name.trim()
      ? t('newLink.nameNeeded')
      : claimsCap != null && (!Number.isInteger(claimsCap) || claimsCap < 1)
        ? t('newTrial.badCap')
        : amount != null && (!Number.isFinite(amount) || amount < 0)
          ? t('cost.badAmount')
          : ''

  const save = async () => {
    setBusy(true)
    setError('')
    try {
      await api.createTrial(accountId, { name: name.trim(), trialDays: days, claimsCap, cost: amount == null ? null : { amount } })
      onCreated(name.trim())
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
      setBusy(false)
    }
  }

  return (
    <Modal
      title={t('newTrial.title')}
      onClose={onClose}
      footer={
        <>
          {error && <p className="dl-error dl-foot-error">{error}</p>}
          <button type="button" className="dl-btn dl-btn--ghost" onClick={onClose}>
            {t('cancel')}
          </button>
          <button type="button" className="dl-btn dl-btn--primary" disabled={busy || Boolean(problem)} title={problem || undefined} onClick={() => void save()}>
            <BusyLabel busy={busy} label={t('saving')}>{t('newLink.create')}</BusyLabel>
          </button>
        </>
      }
    >
      <div className="tl-form">
        <div className="dl-field">
          <span className="dl-label">{t('links.account')}</span>
          <AccountSelect accounts={accounts} value={accountId} onChange={setAccountId} />
        </div>
        <div className="dl-field">
          <label className="dl-label" htmlFor={`${ids}-name`}>{t('newLink.name')}</label>
          <input id={`${ids}-name`} className="dl-input" value={name} maxLength={64} onChange={(e) => setName(e.target.value)} placeholder={t('newTrial.namePlaceholder')} autoFocus />
        </div>
        <div className="tl-form-row">
          <div className="dl-field">
            <label className="dl-label" htmlFor={`${ids}-days`}>{t('newTrial.days')}</label>
            <select id={`${ids}-days`} className="dl-input" value={days} onChange={(e) => setDays(Number(e.target.value))}>
              {TRIAL_DAY_CHOICES.map((d) => (
                <option key={d} value={d}>{t('newTrial.daysOption', { count: d })}</option>
              ))}
            </select>
          </div>
          <div className="dl-field">
            <label className="dl-label" htmlFor={`${ids}-cap`}>{t('newTrial.cap')}</label>
            <input id={`${ids}-cap`} className="dl-input" type="number" min={1} step={1} inputMode="numeric" value={cap} onChange={(e) => setCap(e.target.value)}
              placeholder={t('newTrial.capPlaceholder')} />
          </div>
        </div>
        <span className="dl-hint">{t('newTrial.limitsHint')}</span>
        <CostField id={`${ids}-cost`} value={cost} onChange={setCost} hint={t('newLink.costHint')} />
        <p className="dl-hint">{t('newTrial.onlyFansNote')}</p>
      </div>
    </Modal>
  )
}

// ── Reports ───────────────────────────────────────────────────────────────────

function ReportsTab({ api, accounts }: { api: TrackingLinksApi; accounts: CrmAccount[] | null }) {
  const { t } = useTranslation('trackingLinks')
  const [data, setData] = useState<{ reports: LinkReport[]; public_base: string | null } | null>(null)
  const [error, setError] = useState('')
  const [notice, setNotice] = useState('')
  const [editing, setEditing] = useState<LinkReport | 'new' | null>(null)
  const [revoking, setRevoking] = useState<LinkReport | null>(null)
  const [copied, setCopied] = useState('')
  const byId = useMemo(() => new Map((accounts ?? []).map((a) => [a.account_id, a])), [accounts])

  const load = useCallback(async () => {
    setError('')
    try {
      setData(await api.reports())
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    }
  }, [api])

  useEffect(() => {
    void load()
  }, [load])

  const copy = async (r: LinkReport) => {
    const url = reportUrl(r.token, data?.public_base ?? null)
    try {
      await navigator.clipboard.writeText(url)
      setCopied(r.id)
      window.setTimeout(() => setCopied((c) => (c === r.id ? '' : c)), 1800)
    } catch {
      window.prompt(t('reports.copyPrompt'), url)
    }
  }

  return (
    <>
      <div className="dl-bar">
        <p className="dl-hint tl-reports-hint">{t('reports.hint')}</p>
        <div className="dl-bar-actions">
          <button type="button" className="dl-btn dl-btn--primary" onClick={() => setEditing('new')}>
            <FeatherIcon name="plus" size={14} /> {t('reports.new')}
          </button>
        </div>
      </div>
      {error && <p className="dl-error">{error}</p>}
      {notice && <p className="dl-notice">{notice}</p>}
      {!data ? (
        !error && <LoadingBlock label={t('loading')} />
      ) : data.reports.length === 0 ? (
        <div className="dl-empty">
          <FeatherIcon name="share-2" size={22} />
          <p className="dl-strong">{t('reports.emptyTitle')}</p>
          <p className="dl-muted">{t('reports.emptyBody')}</p>
        </div>
      ) : (
        <div className="dl-cards">
          {data.reports.map((r) => {
            const profiles = [...new Set(r.links.map((l) => l.account_id))].map((id) => (byId.get(id) ? accountName(byId.get(id)!) : id))
            const trials = r.links.filter((l) => l.kind === 'trial').length
            return (
              <article key={r.id} className={`dl-card ${r.expired ? 'tl-expired' : ''}`}>
                <div className="dl-card-top">
                  <div className="dl-card-title">
                    <span className="dl-card-name dl-strong">{r.name}</span>
                    <span className="dl-card-sub dl-muted">
                      {trials
                        ? t('reports.linksMixed', { tracking: r.links.length - trials, trial: trials })
                        : t('reports.links', { count: r.links.length })}{' '}
                      · {profiles.slice(0, 3).join(', ')}
                      {profiles.length > 3 && ` +${profiles.length - 3}`}
                    </span>
                  </div>
                  <div className="dl-card-actions">
                    <button type="button" className="dl-btn dl-btn--small" onClick={() => void copy(r)} disabled={r.expired}>
                      <FeatherIcon name={copied === r.id ? 'check' : 'copy'} size={13} /> {copied === r.id ? t('reports.copied') : t('reports.copy')}
                    </button>
                    <a className="dl-btn dl-btn--small" href={reportUrl(r.token, data.public_base)} target="_blank" rel="noreferrer" aria-disabled={r.expired}>
                      <FeatherIcon name="external-link" size={13} /> {t('reports.open')}
                    </a>
                    <button type="button" className="dl-btn dl-btn--small" onClick={() => setEditing(r)}>
                      <FeatherIcon name="edit-2" size={13} /> {t('reports.edit')}
                    </button>
                    <button type="button" className="dl-btn dl-btn--small dl-btn--danger" onClick={() => setRevoking(r)}>
                      {t('reports.revoke')}
                    </button>
                  </div>
                </div>
                <div className="dl-card-tags">
                  {r.columns.map((c) => (
                    <span key={c} className="dl-pill">{t(`reportCol.${c}`)}</span>
                  ))}
                  <span className={`dl-stat ${r.expired ? 'dl-stat--warn' : ''}`}>
                    {r.expires_at
                      ? r.expired
                        ? t('reports.expired', { date: r.expires_at.slice(0, 10) })
                        : t('reports.expires', { date: r.expires_at.slice(0, 10) })
                      : t('reports.noExpiry')}
                  </span>
                </div>
              </article>
            )
          })}
        </div>
      )}

      {editing && (
        <ReportDialog
          api={api}
          accounts={accounts}
          report={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={(r, isNew) => {
            setEditing(null)
            setNotice(isNew ? t('reports.createdNotice', { name: r.name }) : t('reports.savedNotice', { name: r.name }))
            void load()
          }}
        />
      )}
      {revoking && (
        <ConfirmDialog
          title={t('reports.revokeTitle', { name: revoking.name })}
          confirm={t('reports.revoke')}
          danger
          onClose={() => setRevoking(null)}
          onConfirm={async () => {
            await api.revokeReport(revoking.id)
            setNotice(t('reports.revokedNotice', { name: revoking.name }))
            void load()
          }}
        >
          <p className="dl-hint">{t('reports.revokeBody')}</p>
        </ConfirmDialog>
      )}
    </>
  )
}
